import type { ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { useAuth } from '../auth.tsx';
import { useOnline } from '../hooks.ts';
import { PendingLine, SyncRunner } from './SyncStatus.tsx';

const icon = (path: ReactNode) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {path}
  </svg>
);

const TABS = [
  { href: '/', label: 'Today', match: (l: string) => l === '/', icon: icon(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>) },
  {
    href: '/plan',
    label: 'Plan',
    match: (l: string) => l.startsWith('/plan'),
    icon: icon(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>),
  },
  {
    href: '/progress',
    label: 'Progress',
    match: (l: string) => l.startsWith('/progress'),
    icon: icon(<path d="M4 20h16M7 16v-4M12 16V8M17 16V5" />),
  },
  {
    href: '/exercises',
    label: 'Exercises',
    match: (l: string) => l.startsWith('/exercises'),
    icon: icon(<path d="M6 7v10M3 9v6M18 7v10M21 9v6M6 12h12" />),
  },
  {
    href: '/settings',
    label: 'Settings',
    match: (l: string) => l.startsWith('/settings'),
    icon: icon(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>),
  },
];

export function Layout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const online = useOnline();
  const { state } = useAuth();
  const offline = !online || (state.status === 'signedIn' && state.offline);

  return (
    <>
      <header className="topbar">
        <Link href="/" className="brand">
          <img src="/icon.svg" alt="" width="28" height="28" />
          Squats
        </Link>
      </header>
      {offline && (
        <p className="banner" role="status">
          You’re offline. Showing what’s saved on this device.
        </p>
      )}
      {state.status === 'signedIn' && (
        <>
          <SyncRunner user={state.user} online={!offline} />
          <PendingLine online={!offline} />
        </>
      )}
      <main className="shell with-tabbar">{children}</main>
      <nav className="tabbar" aria-label="Main">
        {TABS.map((t) => (
          <Link key={t.href} href={t.href} className="tab" aria-current={t.match(location) ? 'page' : undefined}>
            {t.icon}
            <span>{t.label}</span>
          </Link>
        ))}
      </nav>
    </>
  );
}

/** Full-screen centered layout for signed-out pages. */
export function Centered({ children }: { children: ReactNode }) {
  return (
    <main className="shell centered">
      <div className="hero">
        <img src="/icon.svg" alt="" width="64" height="64" className="hero-icon" />
        <h1>Squats</h1>
        <p className="muted">Workout planner and training log</p>
      </div>
      {children}
    </main>
  );
}
